import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";

const BACKEND_URL = "http://127.0.0.1:4000";

const AuthContext = createContext(null);

function decodeJwtPayload(token) {
  try {
    const payload = token.split(".")[1];
    if (!payload) return null;

    const base64 = payload.replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), "=");

    const json = atob(padded);
    return JSON.parse(json);
  } catch {
    return null;
  }
}

function toDateFromJwtSeconds(value) {
  if (typeof value !== "number") return null;
  if (!Number.isFinite(value)) return null;
  return new Date(value * 1000);
}

export function AuthProvider({ children }) {
  const [token, setToken] = useState(() => localStorage.getItem("session"));
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(Boolean(token));

  const fetchingRef = useRef(false);

  const logout = useCallback(() => {
    localStorage.removeItem("session");
    setToken(null);
    setUser(null);
    setLoading(false);
  }, []);

  const fetchUser = useCallback(
    async (sessionToken) => {
      if (!sessionToken || fetchingRef.current) return;

      fetchingRef.current = true;
      setLoading(true);

      try {
        const res = await fetch(`${BACKEND_URL}/dashboard`, {
          headers: {
            Authorization: `Bearer ${sessionToken}`,
          },
        });

        if (res.status === 401) {
          logout();
          return;
        }

        if (!res.ok) {
          throw new Error("Failed to fetch dashboard");
        }

        const json = await res.json();
        const payload = decodeJwtPayload(sessionToken) || {};

        const issuedAt = toDateFromJwtSeconds(payload.iat);
        const expiresAt = toDateFromJwtSeconds(payload.exp);

        setUser({
          userId: json.user_id,
          email: payload.email || payload.mail || null,
          name: payload.name || payload.given_name || null,

          iat: issuedAt,
          exp: expiresAt,
          iatText: issuedAt ? issuedAt.toLocaleString() : "-",
          expText: expiresAt ? expiresAt.toLocaleString() : "-",
        });
      } finally {
        fetchingRef.current = false;
        setLoading(false);
      }
    },
    [logout]
  );

  const login = useCallback(
    async (sessionToken) => {
      localStorage.setItem("session", sessionToken);
      setToken(sessionToken);
      await fetchUser(sessionToken);
    },
    [fetchUser]
  );

  useEffect(() => {
    if (!token) return;
    if (user) return;
    fetchUser(token);
  }, [token, user, fetchUser]);

  const value = useMemo(
    () => ({
      token,
      user,
      loading,
      login,
      logout,
    }),
    [token, user, loading, login, logout]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within an AuthProvider");
  return ctx;
}
